import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { SITE } from "@/lib/site-config";

const timelineEvents = [
  { time: "2:14 PM", label: "Missed call", detail: "From (312) 555-0148", tone: "signal" as const },
  { time: "2:14 PM", label: "Automatic SMS sent", detail: "\u201cSorry we missed you \u2014 what can we help with?\u201d", tone: "brand" as const },
  { time: "2:17 PM", label: "Customer replies", detail: "\u201cNeed a quote for a water heater\u201d", tone: "neutral" as const },
  { time: "2:19 PM", label: "AI qualifies the lead", detail: "Ready to send an estimate", tone: "success" as const },
];

const toneDot: Record<string, string> = {
  signal: "bg-signal-500",
  brand: "bg-brand-500",
  neutral: "bg-ink-soft",
  success: "bg-success-500",
};

export function Hero() {
  return (
    <section className="container-page grid items-center gap-12 py-20 md:grid-cols-2 md:py-28">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-brand-500">
          For businesses that take phone calls
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-ink md:text-5xl">
          A missed call doesn&apos;t have to mean a lost customer.
        </h1>
        <p className="mt-5 max-w-prose text-lg text-ink-soft">
          {SITE.productName} automatically follows up every missed call with a text message,
          lets the customer reply, and qualifies the lead against your rules \u2014 so your team
          only spends time on real opportunities, even when no one could pick up the phone.
        </p>
        <p className="mt-3 max-w-prose text-sm text-ink-soft">
          Built for home services, clinics, salons, auto shops, and any business where a
          ringing phone is a sales opportunity.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to={SITE.signupHref}>
            <Button className="px-6 py-3 text-base">Get started</Button>
          </Link>
          <a href={SITE.demoHref}>
            <Button variant="secondary" className="px-6 py-3 text-base">
              Book a demo
            </Button>
          </a>
        </div>
        <p className="mt-4 text-xs text-ink-soft">No new phone number or hardware required.</p>
      </div>

      <div className="rounded-xl border border-line bg-paper-raised p-6 shadow-card">
        <p className="mb-5 text-sm font-medium text-ink-soft">What happens after a missed call</p>
        <ol className="space-y-5">
          {timelineEvents.map((event, i) => (
            <li key={event.label} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className={`mt-1 h-2.5 w-2.5 rounded-full ${toneDot[event.tone]}`} />
                {i < timelineEvents.length - 1 && <span className="w-px flex-1 bg-line" />}
              </div>
              <div className="pb-1">
                <p className="text-xs text-ink-soft">{event.time}</p>
                <p className="text-sm font-medium text-ink">{event.label}</p>
                <p className="text-sm text-ink-soft">{event.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const reasons = [
  { title: "Employees are busy", detail: "Helping another customer in person or on another line." },
  { title: "Already on a call", detail: "There's only one front desk phone and it's already in use." },
  { title: "They're driving", detail: "Technicians and service teams are on the road, not at a desk." },
  { title: "Hands-on work", detail: "Mid-job, mid-appointment, or otherwise away from the phone." },
  { title: "The business is closed", detail: "Nights, weekends, and holidays still generate calls." },
];

export function Problem() {
  return (
    <section className="border-t border-line bg-paper-raised py-20">
      <div className="container-page">
        <div className="max-w-prose">
          <h2 className="font-display text-3xl text-ink">
            Every unanswered call is a customer who might call someone else next.
          </h2>
          <p className="mt-4 text-ink-soft">
            Missing a call is rarely a sign anything is wrong \u2014 it&apos;s just what happens
            when a business is busy. But to the person calling, a missed call is a missed
            connection. If nobody follows up quickly, that potential customer often finds a
            competitor who answers first.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {reasons.map((reason) => (
            <div key={reason.title} className="rounded-lg border border-line bg-paper p-4">
              <p className="text-sm font-medium text-ink">{reason.title}</p>
              <p className="mt-1 text-xs text-ink-soft">{reason.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const steps = [
  {
    title: "Customer calls",
    detail: "A potential customer calls your business number, same as always.",
  },
  {
    title: "Business misses the call",
    detail: "No one is able to pick up \u2014 busy, on another line, on the road, or closed.",
  },
  {
    title: "Automatic SMS is sent",
    detail: "Within seconds, the caller gets a text asking what they need. No manual step.",
  },
  {
    title: "Customer replies",
    detail: "The conversation continues over text, on the customer's own time.",
  },
  {
    title: "AI qualifies the lead",
    detail: "Following the rules your business sets, the reply is checked against what makes a lead worth your team's time.",
  },
  {
    title: "Business receives the lead",
    detail: "A qualified lead \u2014 contact info, what they need, and the full conversation \u2014 lands in your dashboard.",
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20">
      <div className="container-page">
        <h2 className="font-display text-3xl text-ink">How it works</h2>
        <p className="mt-3 max-w-prose text-ink-soft">
          Six steps, none of which need anyone on your team to lift a finger until there's a
          real lead to look at.
        </p>
        <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span className="font-display text-2xl text-brand-300">{i + 1}</span>
              <div>
                <p className="font-medium text-ink">{step.title}</p>
                <p className="mt-1 text-sm text-ink-soft">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
